/**
 * Error Fallback Component
 *
 * Displayed when an unhandled error occurs in the React tree.
 * Used with Sentry Error Boundary to provide a user-friendly error page.
 */

import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        {/* Error Icon */}
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="h-10 w-10 text-amber-600" />
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">Oops, something went wrong</h1>
          <p className="text-gray-600">The app encountered an issue. We've been notified and are working on a fix.</p>
        </div>

        {/* Error Details (Development only) */}
        {import.meta.env.DEV && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-left">
            <p className="text-sm font-medium text-red-800">Error details:</p>
            <pre className="mt-2 overflow-auto text-xs text-red-700">{error.message}</pre>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={handleRefresh} className="gap-2 bg-amber-500 hover:bg-amber-600">
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
          <Button
            variant="outline"
            onClick={handleGoHome}
            className="gap-2 border-amber-300 hover:bg-amber-50"
          >
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-sm text-gray-500">If the problem persists, try clearing your browser cache or contact support.</p>
      </div>
    </div>
  );
}
